import { useContextMenuMESF } from "@dexteel/mesf-core";
import { Grid, Typography } from "@mui/material";
import {
  DefaultMenuItem,
  GetContextMenuItemsParams,
  GridApi,
  IRowNode,
  MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGridDefinitions } from "../../../../../controls/ag-grid/components/GridDefinitions";
import { FooterButton } from "../../../../../controls/buttons/FooterButton";
import { TextFieldSearchFilter } from "../../../../../shared/TextFieldSearchFilter";
import { CatalogMold } from "../../../models/CatalogMold";
import { useMoldOptionsFunctions } from "../hooks/useMoldOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { MoldModal } from "./MoldModal";

type onModalHideProps = {
  entityId?: number;
  shouldUpdate?: boolean;
};

type Props = {
  getCatalogMoldsFromAPI: Function;
  isLoading: boolean;
  moldId: String | undefined;
};

export const TableMold = ({
  getCatalogMoldsFromAPI,
  isLoading,
  moldId,
}: Props) => {
  const navigate = useNavigate();

  const [filterValue, setFilterValue] = useState("");
  const [showModal, setShowModal] = useState<"new" | "edit" | "delete" | "">(
    "",
  );
  const [mold, setMold] = useState<CatalogMold | null>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const { showContextMenu, registerConfig } = useContextMenuMESF();

  const handleNewMold = () => {
    if (gridApi) gridApi.deselectAll();
    setShowModal("new");
    setMold(null);
    navigate(`/configuration/caster-molds/new`);
  };

  const { getMenuOptions } = useMoldOptionsFunctions({
    setMold,
    setShowModal,
    handleNewMold,
  });
  const { rows, columnDefs } = useTableData({
    setMold,
    setShowModal,
    showContextMenu,
  });
  const { columnSimpleDefaults } = useGridDefinitions({
    OnEdit: (data: any) => {
      setShowModal("edit");
      setMold(data);
    },
  });

  const onRowDoubleClicked = (event: any) => {
    const cell = gridApi?.getFocusedCell();
    if (cell?.column.getColId() !== "-" && event.node.isSelected()) {
      setMold(event.data);
      setShowModal("edit");
      navigate(
        `/configuration/caster-molds/${(event.data as CatalogMold).MoldId}`,
      );
    }
  };

  const onCellClicked = (event: any) => {
    if (event.column.colId === "-")
      navigate(
        `/configuration/caster-molds/${(event.data as CatalogMold).MoldId}`,
      );
  };

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
      const data = params.node?.data;
      const nativeEvent = (
        params as GetContextMenuItemsParams & { event?: MouseEvent }
      ).event;

      if (data && nativeEvent) {
        showContextMenu(
          nativeEvent as unknown as React.MouseEvent,
          data,
          "moldContext",
        );
        params.api.deselectAll();
        params.node?.setSelected(true);
      }
      return [];
    },
    [rows],
  );

  const onModalHide = ({ entityId, shouldUpdate }: onModalHideProps) => {
    if (entityId && shouldUpdate) {
      setTimeout(() => {
        const rowNode = gridApi?.getRowNode(String(entityId));
        if (rowNode) {
          gridApi?.ensureIndexVisible(rowNode.rowIndex ?? 0, "middle");
          rowNode?.setSelected(true);
        }
      }, 0);
    }
    setShowModal("");
    setMold(null);
    navigate(`/configuration/caster-molds/`);
  };

  useEffect(() => {
    if (moldId) {
      if (moldId === "new") {
        handleNewMold();
      } else {
        let moldNode: IRowNode<any>;

        gridApi?.forEachNode((n) => {
          if ((n.data as CatalogMold).MoldId === Number(moldId)) {
            moldNode = n;
            return true;
          }
        });

        if (moldNode!) {
          const pageSize = gridApi?.paginationGetPageSize();
          const pageNumber = Math.floor((moldNode!.rowIndex ?? 0) / pageSize!);
          gridApi?.paginationGoToPage(pageNumber);

          moldNode.setSelected(true);
          setMold(moldNode.data);
          setShowModal("edit");
        }
      }
    }
  }, [gridApi]);

  useEffect(() => {
    registerConfig({
      id: "moldContext",
      getOptions: getMenuOptions,
    });
  }, []);

  return (
    <>
      <Grid container justifyContent="center">
        <Grid item md={12} xs={12} style={{ padding: "0 15px" }}>
          <Typography
            variant="h5"
            style={{ margin: "1px 0 10px", fontWeight: 600 }}
          >
            Molds
          </Typography>
        </Grid>
        <Grid item md={12} xs={12} style={{ margin: "10px 18px" }}>
          <TextFieldSearchFilter
            value={filterValue}
            onChange={(val) => {
              setFilterValue(val);
              gridApi?.setGridOption("quickFilterText", val);
            }}
          />
        </Grid>
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          style={{ width: "100%", padding: "0 15px" }}
        >
          <div style={{ width: "100%", height: "70vh" }}>
            <AgGridReact<CatalogMold>
              rowData={rows || []}
              columnDefs={columnDefs}
              defaultColDef={columnSimpleDefaults}
              rowHeight={34}
              headerHeight={34}
              animateRows
              rowSelection="single"
              getContextMenuItems={getContextMenuItems}
              onRowDoubleClicked={onRowDoubleClicked}
              onGridReady={(params) => setGridApi(params.api)}
              getRowId={(params) => String(params.data.MoldId)}
              loading={isLoading}
              onCellClicked={onCellClicked}
            />
          </div>
          <FooterButton label="MOLD" onClick={handleNewMold} />
        </Grid>
      </Grid>
      <MoldModal
        mold={mold}
        showModal={showModal}
        onHide={({ entityId, shouldUpdate }) => {
          onModalHide({ entityId, shouldUpdate });
        }}
        refreshData={getCatalogMoldsFromAPI}
      />
    </>
  );
};
